import { ReactNode } from "react";
import { Star, ShieldCheck, CheckCircle2 } from "lucide-react";

// v3.9.16 — one consistent required-field marker used across every form.
export function Req() {
  return <span className="text-brand-red" title="Required" aria-label="required">*</span>;
}

export function Badge({ children, tone = "gray" }: { children: ReactNode; tone?: "gray" | "green" | "red" | "gold" | "navy" }) {
  const map: Record<string, string> = {
    gray: "bg-navy-50 text-navy-600",
    green: "bg-brand-green/10 text-brand-greendark",
    red: "bg-brand-red/10 text-brand-reddark",
    gold: "bg-brand-gold/15 text-brand-gold",
    navy: "bg-navy text-white",
  };
  return <span className={`chip ${map[tone]}`}>{children}</span>;
}

export function Verified({ label = "Verified", iconOnly = false }: { label?: string; iconOnly?: boolean }) {
  return (
    <span
      className={`${iconOnly ? "inline-flex shrink-0 items-center justify-center text-brand-greendark" : "chip bg-brand-green/10 text-brand-greendark"}`}
      title={iconOnly ? label : undefined}
      aria-label={iconOnly ? label : undefined}
    >
      <ShieldCheck className={iconOnly ? "h-4 w-4 fill-brand-green/10" : "w-3.5 h-3.5"} />{!iconOnly && <> {label}</>}
    </span>
  );
}

export function Rating({ value, count }: { value: number; count?: number }) {
  return (
    <span className="inline-flex items-center gap-1 text-sm">
      <Star className="w-4 h-4 fill-brand-gold text-brand-gold" />
      <b className="text-ink">{value.toFixed(1)}</b>
      {count != null && <span className="text-mut">({count})</span>}
    </span>
  );
}

export function Avatar({ initials, size = 44 }: { initials: string; size?: number }) {
  return (
    <div
      className="rounded-full bg-navy-50 text-navy font-bold flex items-center justify-center shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.36 }}
    >
      {initials}
    </div>
  );
}

export function Feature({ children }: { children: ReactNode }) {
  return (
    <li className="flex items-start gap-2 text-sm text-mut">
      <CheckCircle2 className="w-4 h-4 mt-0.5 text-brand-green shrink-0" /> <span>{children}</span>
    </li>
  );
}

export function Section({
  title,
  action,
  children,
  titleClassName = "",
  actionClassName = "",
  headerClassName = "",
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  titleClassName?: string;
  actionClassName?: string;
  headerClassName?: string;
}) {
  return (
    <section className="container-x py-8 sm:py-12">
      <div className={`section-header flex justify-between gap-3 mb-5 ${headerClassName || "items-end"}`}>
        <h2 className={`section-title sec-title min-w-0 ${titleClassName}`}>{title}</h2>
        {action && <div className={`section-action shrink-0 ${actionClassName}`}>{action}</div>}
      </div>
      {children}
    </section>
  );
}

export function Tabs({ tabs, active, onChange, labelForTab = (tab) => tab }: { tabs: string[]; active: string; onChange: (t: string) => void; labelForTab?: (tab: string) => string }) {
  return (
    <div className="flex gap-1 overflow-x-auto no-scrollbar border-b border-line">
      {tabs.map((t) => (
        <button
          key={t}
          type="button"
          onClick={() => onChange(t)}
          className={`px-4 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 -mb-px transition-colors ${
            active === t ? "border-brand-red text-navy" : "border-transparent text-mut hover:text-navy"
          }`}
        >
          {labelForTab(t)}
        </button>
      ))}
    </div>
  );
}
